import React from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerFooter,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  VStack,
  Box,
  Text,
  Button,
  Flex,
  useColorModeValue,
  IconButton,
} from '@chakra-ui/react';
import { FaSave, FaCheck, FaVolumeUp } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { WordPopup } from '../smart-read/WordPopup';
import { useWordTranslation, UseWordTranslationProps } from '../../hooks/useWordTranslation';
import { useLanguage } from '../../contexts/LanguageContext';

interface WordTranslationProviderProps extends UseWordTranslationProps {
  children: (formatContent: (content: string) => React.ReactNode) => React.ReactNode;
  /** Override close handler for external control */
  onClose?: () => void;
}

export function WordTranslationProvider({
  children,
  sourceLanguage,
  preloadedTranslations = [],
  onClose
}: WordTranslationProviderProps) {
  const { t } = useTranslation();
  const { userLanguage } = useLanguage();

  const {
    selectedWord,
    selectedWordData,
    isPlayingWord,
    isSavingWord,
    isMarkingWord,
    isMobile,
    isWordDrawerOpen,
    onWordDrawerClose,
    handleWordClick,
    handlePlayPronunciation,
    handleSaveWord,
    handleMarkKnown,
    handleClosePopups,
  } = useWordTranslation({ sourceLanguage, preloadedTranslations });

  // Colors
  const drawerBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Format content with clickable words
  const formatContent = React.useCallback((content: string): React.ReactNode => {
    if (!content) return content;

    // Split content by spaces to identify words, preserving punctuation
    const parts = content.split(/(\s+)/);

    return parts.map((part, index) => {
      // If it's a space/whitespace, render it as is
      if (/^\s+$/.test(part)) {
        return <React.Fragment key={index}>{part}</React.Fragment>;
      }

      // Check if this part is an actual word or contains punctuation
      // This regex matches a word pattern that might have punctuation attached
      const wordMatch = part.match(/^([\p{L}\p{M}][\p{L}\p{M}\-']*)([\p{P}\p{S}]*)$/u);

      if (wordMatch) {
        const [, word, punctuation] = wordMatch;
        const isSelected = selectedWord === word;

        return (
          <React.Fragment key={index}>
            <Box
              as="span"
              display="inline"
              position="relative"
              px={1}
              py={isMobile ? 1 : 0.5}
              mx={-1}
              borderRadius="md"
              bg={isSelected ? 'purple.100' : 'transparent'}
              color={isSelected ? 'purple.600' : 'inherit'}
              fontWeight={isSelected ? 'bold' : 'inherit'}
              cursor="pointer"
              _hover={{ bg: 'purple.50' }}
              onClick={(e: React.MouseEvent<HTMLSpanElement>) => handleWordClick(e, word)}
              data-word={word}
            >
              {word}
            </Box>
            {punctuation}
          </React.Fragment>
        );
      }

      // If not a typical word pattern, just render as is
      return <React.Fragment key={index}>{part}</React.Fragment>;
    });
  }, [selectedWord, isMobile, handleWordClick]);

  // Handle close with external override
  const handleClose = React.useCallback(() => {
    handleClosePopups();
    if (onClose) {
      onClose();
    }
  }, [handleClosePopups, onClose]);

  return (
    <>
      {children(formatContent)}

      {/* Debug logging */}
      {React.useEffect(() => {
        if (selectedWord) {
          console.log('WordTranslationProvider render state:', {
            selectedWord,
            hasSelectedWordData: !!selectedWordData,
            isMobile,
            isWordDrawerOpen,
            shouldRenderDesktopPopup: !isMobile && selectedWord && selectedWordData,
            shouldRenderMobileDrawer: isMobile,
            drawerIsOpen: isWordDrawerOpen
          });
        }
      }, [selectedWord, selectedWordData, isMobile, isWordDrawerOpen])}

      {/* Desktop Word Popup */}
      {!isMobile && selectedWord && selectedWordData && (
        <WordPopup
          word={selectedWord}
          data={selectedWordData}
          onClose={handleClose}
          onPlayPronunciation={() => selectedWord && handlePlayPronunciation(selectedWord)}
          onSaveWord={() => selectedWord && handleSaveWord(selectedWord)}
          onMarkKnown={() => selectedWord && handleMarkKnown(selectedWord)}
          isPlayingWord={isPlayingWord}
          userLanguage={userLanguage}
        />
      )}

      {/* Mobile Word Drawer */}
      {isMobile && (
        <Drawer
          isOpen={isWordDrawerOpen}
          placement="bottom"
          onClose={handleClose}
          size="sm"
        >
          <DrawerOverlay />
          <DrawerContent bg={drawerBg} borderTopRadius="xl" borderColor={borderColor}>
            <DrawerCloseButton />
            <DrawerHeader borderBottomWidth="1px" borderColor={borderColor}>
              <Text fontSize="lg" fontWeight="bold">
                {selectedWord}
              </Text>
            </DrawerHeader>

            <DrawerBody py={4}>
              {selectedWordData && (
                <VStack spacing={4} align="stretch">
                  {/* Show translations based on user language */}
                  {userLanguage === 'nl' && selectedWordData.nl && (
                    <Box>
                      <Text fontSize="sm" color="gray.500" mb={1}>
                        {t('smartRead.translation')}
                      </Text>
                      <Text fontSize="md" fontWeight="medium">
                        {selectedWordData.nl}
                      </Text>
                    </Box>
                  )}

                  {userLanguage === 'he' && selectedWordData.he && (
                    <Box>
                      <Text fontSize="sm" color="gray.500" mb={1}>
                        {t('smartRead.translation')}
                      </Text>
                      <Text fontSize="md" fontWeight="medium">
                        {selectedWordData.he}
                      </Text>
                    </Box>
                  )}

                  {userLanguage === 'en' && selectedWordData.en && (
                    <Box>
                      <Text fontSize="sm" color="gray.500" mb={1}>
                        {t('smartRead.translation')}
                      </Text>
                      <Text fontSize="md" fontWeight="medium">
                        {selectedWordData.en}
                      </Text>
                    </Box>
                  )}

                  {/* Show all available translations if no match for user language */}
                  {(!selectedWordData[userLanguage as keyof typeof selectedWordData]) && (
                    <>
                      {selectedWordData.nl && (
                        <Box>
                          <Text fontSize="sm" color="gray.500" mb={1}>
                            Dutch
                          </Text>
                          <Text fontSize="md" fontWeight="medium">
                            {selectedWordData.nl}
                          </Text>
                        </Box>
                      )}
                      {selectedWordData.he && (
                        <Box>
                          <Text fontSize="sm" color="gray.500" mb={1}>
                            Hebrew
                          </Text>
                          <Text fontSize="md" fontWeight="medium">
                            {selectedWordData.he}
                          </Text>
                        </Box>
                      )}
                      {selectedWordData.en && (
                        <Box>
                          <Text fontSize="sm" color="gray.500" mb={1}>
                            English
                          </Text>
                          <Text fontSize="md" fontWeight="medium">
                            {selectedWordData.en}
                          </Text>
                        </Box>
                      )}
                    </>
                  )}
                </VStack>
              )}
            </DrawerBody>

            <DrawerFooter borderTopWidth="1px" borderColor={borderColor}>
              <Flex gap={2} width="full">
                <IconButton
                  icon={<FaVolumeUp />}
                  onClick={() => selectedWord && handlePlayPronunciation(selectedWord)}
                  isLoading={isPlayingWord}
                  aria-label={t('smartRead.wordPopup.playPronunciation')}
                  colorScheme="blue"
                  size="sm"
                />
                <Button
                  leftIcon={<FaSave />}
                  colorScheme="blue"
                  size="sm"
                  flex={1}
                  onClick={() => selectedWord && handleSaveWord(selectedWord)}
                  isLoading={isSavingWord}
                  loadingText={t('smartRead.saving')}
                >
                  {t('smartRead.saveWord')}
                </Button>
                <Button
                  leftIcon={<FaCheck />}
                  colorScheme="green"
                  size="sm"
                  flex={1}
                  onClick={() => selectedWord && handleMarkKnown(selectedWord)}
                  isLoading={isMarkingWord}
                  loadingText={t('smartRead.marking')}
                >
                  {t('smartRead.markKnown')}
                </Button>
              </Flex>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      )}
    </>
  );
}